import React from 'react';
import { Button } from '../ui/button';
import { Progress } from '../ui/progress';
import { Badge } from '../ui/badge';
import { ArrowLeft, Clock, CheckCircle2 } from 'lucide-react';
import { cn } from '../../lib/utils';

/**
 * Reusable Workflow Header Component
 * Used across DSR, Vendor, Breach, ROPA, DPIA, AI Assessment workflows
 */
export function WorkflowHeader({
  title,
  subtitle,
  reference,
  onBack,
  progressPercentage = 0,
  autoSaveStatus = 'idle', // 'idle' | 'saving' | 'saved'
  riskScore,
  riskTier,
  riskTierLabel,
  riskTierColor,
  showRisk = false,
  children, // Additional content to render in header
}) {
  return (
    <div className="sticky top-0 z-50 bg-background border-b">
      <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 py-4">
        <div className="flex items-center justify-between gap-4">
          {/* Left: Back button and title */}
          <div className="flex items-center gap-4">
            <Button variant="ghost" size="icon" onClick={onBack}>
              <ArrowLeft className="h-5 w-5" />
            </Button>
            <div>
              <h1 className="text-xl font-bold">{title}</h1>
              <p className="text-sm text-muted-foreground">
                {reference || subtitle}
              </p>
            </div>
          </div>

          {/* Right: Progress, Risk, Auto-save */}
          <div className="flex items-center gap-4">
            {/* Risk Score Display (optional) */}
            {showRisk && riskScore !== null && riskScore !== undefined && (
              <div className="hidden sm:flex items-center gap-2 px-3 py-1 bg-muted rounded-lg">
                <span className="text-sm font-medium">Risk: {riskScore?.toFixed?.(1) || riskScore}</span>
                {riskTier && (
                  <Badge className={cn('text-xs', riskTierColor)}>
                    {riskTierLabel || riskTier}
                  </Badge>
                )}
              </div>
            )}

            {/* Progress Bar */}
            <div className="hidden sm:flex items-center gap-3 min-w-[150px]">
              <Progress value={progressPercentage} className="h-2 flex-1" />
              <span className={cn(
                "text-sm font-medium min-w-[45px] text-right",
                progressPercentage === 100 ? "text-green-500" : "text-muted-foreground"
              )}>
                {progressPercentage}%
              </span>
            </div>

            {/* Auto-save Status */}
            <AutoSaveIndicator status={autoSaveStatus} />

            {/* Additional content */}
            {children}
          </div>
        </div>
      </div>
    </div>
  );
}

/**
 * Auto-save status indicator
 */
export function AutoSaveIndicator({ status = 'idle' }) {
  return (
    <div className="flex items-center gap-2 text-sm text-muted-foreground min-w-[80px]">
      {status === 'saving' && (
        <>
          <Clock className="h-4 w-4 animate-spin" />
          <span className="hidden sm:inline">Saving...</span>
        </>
      )}
      {status === 'saved' && (
        <>
          <CheckCircle2 className="h-4 w-4 text-green-500" />
          <span className="hidden sm:inline text-green-500">Saved</span>
        </>
      )}
    </div>
  );
}

/**
 * Workflow Navigation (Previous/Next buttons)
 */
export function WorkflowNavigation({
  onPrevious,
  onNext,
  onCancel,
  onSaveDraft,
  onSaveAndClose,
  isFirstTab,
  isLastTab,
  saving = false,
  cancelPath,
}) {
  return (
    <div className="flex items-center justify-between pt-4 border-t">
      <Button
        variant="outline"
        onClick={onPrevious}
        disabled={isFirstTab}
      >
        <ArrowLeft className="h-4 w-4 mr-2" />
        Previous
      </Button>

      <div className="flex gap-2">
        <Button variant="outline" onClick={onCancel}>
          Cancel
        </Button>
        <Button variant="outline" onClick={onSaveDraft} disabled={saving}>
          Save as Draft
        </Button>
        {isLastTab ? (
          <Button onClick={onSaveAndClose} disabled={saving}>
            {saving ? 'Saving...' : 'Save & Close'}
          </Button>
        ) : (
          <Button onClick={onNext}>
            Next
            <ArrowLeft className="h-4 w-4 ml-2 rotate-180" />
          </Button>
        )}
      </div>
    </div>
  );
}

export default WorkflowHeader;
